// Marketing Hub - Planning Éditorial
// Calendrier de contenu avec vue mensuelle et gestion des publications

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Calendar,
  Plus,
  ChevronLeft,
  ChevronRight,
  FileText,
  Linkedin,
  Facebook,
  Instagram,
  Mail,
  Globe,
  Video,
  Image,
  Filter,
  LayoutGrid,
  List,
  Loader2,
  MoreVertical,
  Edit,
  Archive,
  Play,
  Send,
  Clock,
  CheckCircle,
  AlertCircle,
  Sparkles,
  Target,
  Wand2,
  Eye,
  Trash2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Icons par type de contenu
const TYPE_ICONS = {
  article: FileText,
  post_linkedin: Linkedin,
  post_facebook: Facebook,
  post_instagram: Instagram,
  email: Mail,
  page: Globe,
  video: Video,
  infographie: Image,
};

// Couleurs par type
const TYPE_COLORS = {
  article: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  post_linkedin: "bg-sky-500/20 text-sky-400 border-sky-500/30",
  post_facebook: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
  post_instagram: "bg-pink-500/20 text-pink-400 border-pink-500/30",
  email: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  page: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  video: "bg-red-500/20 text-red-400 border-red-500/30",
  infographie: "bg-purple-500/20 text-purple-400 border-purple-500/30",
};

// Couleurs par statut
const STATUS_COLORS = {
  idea: "bg-zinc-500/20 text-zinc-400",
  draft: "bg-amber-500/20 text-amber-400",
  review: "bg-blue-500/20 text-blue-400",
  ready: "bg-emerald-500/20 text-emerald-400",
  scheduled: "bg-purple-500/20 text-purple-400",
  published: "bg-green-500/20 text-green-400",
  archived: "bg-zinc-500/20 text-zinc-400",
};

const STATUS_LABELS = {
  idea: "Idée",
  draft: "Brouillon",
  review: "Relecture",
  ready: "Prêt",
  scheduled: "Planifié",
  published: "Publié",
  archived: "Archivé",
};

const TYPE_LABELS = {
  article: "Article",
  post_linkedin: "LinkedIn",
  post_facebook: "Facebook",
  post_instagram: "Instagram",
  email: "Email",
  page: "Page web",
  video: "Vidéo",
  infographie: "Infographie",
};

const PRIORITY_COLORS = {
  low: "text-zinc-400",
  medium: "text-blue-400",
  high: "text-amber-400",
  urgent: "text-red-400",
};

// Jours de la semaine
const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", 
                "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export default function PlanningEditorial() {
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("calendar"); // calendar | list
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // États pour génération de contenu
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateInstructions, setGenerateInstructions] = useState("");
  const [generateTone, setGenerateTone] = useState("professional");
  
  // États pour visualisation du contenu
  const [contentDialogOpen, setContentDialogOpen] = useState(false);
  const [viewingContent, setViewingContent] = useState(null);
  
  // Data for forms
  const [activities, setActivities] = useState([]);
  const [connections, setConnections] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const month = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      
      const [plansRes, statsRes, activitiesRes, connectionsRes] = await Promise.all([
        axios.get(`${API}/api/marketing/editorial`, { params: { month } }),
        axios.get(`${API}/api/marketing/editorial/stats`),
        axios.get(`${API}/api/marketing/config/activities`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/connections`).catch(() => ({ data: [] })),
      ]);
      
      // Handle both formats: array or {planning: []}
      const plansData = Array.isArray(plansRes.data) ? plansRes.data : (plansRes.data?.planning || []);
      setPlans(plansData);
      setStats(statsRes.data);
      setActivities(activitiesRes.data);
      setConnections(connectionsRes.data.filter(c => 
        ["wordpress", "linkedin", "facebook", "instagram"].includes(c.integration_type)
      ));
    } catch (err) {
      console.error("Erreur chargement planning:", err);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const openCreateDialog = (date = null) => {
    setSelectedDate(date);
    setCreateDialogOpen(true);
  };

  const openEditDialog = (plan) => {
    setSelectedPlan(plan);
    setEditDialogOpen(true);
  };

  const handleDelete = async (planId) => {
    if (!window.confirm("Archiver ce contenu planifié ?")) return;
    try {
      await axios.delete(`${API}/api/marketing/editorial/${planId}`);
      toast.success("Contenu archivé dans Zoho");
      loadData();
    } catch (err) {
      toast.error("Erreur lors de l'archivage");
    }
  };

  const handlePurge = async (planId) => {
    try {
      await axios.delete(`${API}/api/marketing/editorial/${planId}/purge?confirm=PURGE`);
      toast.success("Contenu supprimé définitivement");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  // Ouvrir le dialog de génération de contenu
  const openGenerateDialog = (plan) => {
    setSelectedPlan(plan);
    setGenerateInstructions("");
    setGenerateTone("professional");
    setGenerateDialogOpen(true);
  };

  // Générer du contenu pour un plan
  const handleGenerateContent = async () => {
    if (!selectedPlan) return;
    
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/api/marketing/editorial/${selectedPlan.id}/generate-content`,
        {
          tone: generateTone,
          additional_instructions: generateInstructions || null
        }
      );
      
      if (response.data.success) {
        toast.success("Contenu généré avec succès!");
        setGenerateDialogOpen(false);
        loadData();
        // Ouvrir automatiquement la vue du contenu
        setViewingContent({
          ...selectedPlan,
          generated_content: response.data.generated_content
        });
        setContentDialogOpen(true);
      } else {
        toast.error(response.data.message || "Erreur de génération");
      }
    } catch (err) {
      console.error("Erreur génération:", err);
      toast.error(err.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setGenerating(false);
    }
  };

  // Voir le contenu d'un plan
  const openContentDialog = async (plan) => {
    try {
      const response = await axios.get(`${API}/api/marketing/editorial/${plan.id}/content`);
      if (response.data.has_content) {
        setViewingContent({
          ...plan,
          generated_content: response.data.content?.generated_content || plan.generated_content
        });
        setContentDialogOpen(true);
      } else {
        // Si pas de contenu, proposer de générer
        openGenerateDialog(plan);
      }
    } catch (err) {
      console.error("Erreur récupération contenu:", err);
      if (plan.generated_content) {
        setViewingContent(plan);
        setContentDialogOpen(true);
      } else {
        openGenerateDialog(plan);
      }
    }
  };

  const handlePublish = async (planId) => {
    try {
      await axios.post(`${API}/api/marketing/editorial/${planId}/publish`);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur de publication");
    }
  };

  // Filtrer les plans
  const filteredPlans = plans.filter(p => {
    if (filterStatus && p.status !== filterStatus) return false;
    if (filterType && p.content_type !== filterType) return false;
    return true;
  });

  // Générer le calendrier
  const generateCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Ajuster pour commencer le lundi
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    
    const days = [];
    
    // Jours du mois précédent
    const prevMonth = new Date(year, month, 0);
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }
    
    // Jours du mois courant
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Jours du mois suivant
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  };

  const getPlansForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return filteredPlans.filter(p => p.target_date?.startsWith(dateStr));
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const calendarDays = generateCalendar();

  return (
    <div className="space-y-6" data-testid="planning-editorial">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Planning Éditorial</h1>
          <p className="text-zinc-400 mt-1">Planifiez et suivez vos contenus marketing</p>
        </div>
        <Button onClick={() => openCreateDialog()} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau contenu
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-sm text-zinc-400">Total contenus</p>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
            <p className="text-2xl font-bold text-amber-400">{stats.by_status?.draft || 0}</p>
            <p className="text-sm text-zinc-400">En cours</p>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
            <p className="text-2xl font-bold text-emerald-400">{stats.by_status?.ready || 0}</p>
            <p className="text-sm text-zinc-400">Prêts</p>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
            <p className="text-2xl font-bold text-purple-400">{stats.upcoming_7_days || 0}</p>
            <p className="text-sm text-zinc-400">À publier (7j)</p>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToPrevMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday}>
            Aujourd'hui
          </Button>
          <Button variant="outline" size="sm" onClick={goToNextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <span className="text-lg font-semibold text-white ml-4">
            {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Filtres */}
          <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full max-w-[140px] bg-zinc-800/50 border-zinc-700">
              <SelectValue placeholder="Tous statuts" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="all">Tous statuts</SelectItem>
              {Object.entries(STATUS_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full max-w-[140px] bg-zinc-800/50 border-zinc-700">
              <SelectValue placeholder="Tous types" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="all">Tous types</SelectItem>
              {Object.entries(TYPE_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Vue */}
          <div className="flex bg-zinc-800/50 rounded-lg p-1">
            <Button
              variant={viewMode === "calendar" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("calendar")}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("list")}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
        </div>
      ) : viewMode === "calendar" ? (
        /* Vue Calendrier
           Mobile (<md) : liste verticale des jours qui ont au moins un
             plan, ou tous les jours du mois si on est entre deux. La
             grille 7 colonnes est inutilisable en dessous de 480 px.
           Tablet/Desktop (≥md) : grille 7 colonnes classique. */
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
          {/* Header jours — masqué sur mobile (la liste verticale a déjà la date) */}
          <div className="hidden md:grid md:grid-cols-7 border-b border-zinc-800">
            {DAYS.map(day => (
              <div key={day} className="px-2 py-3 text-center text-sm font-medium text-zinc-400">
                {day}
              </div>
            ))}
          </div>

          {/* Grille — flex-col en mobile (1 jour par ligne), grid 7 en md+ */}
          <div className="flex flex-col md:grid md:grid-cols-7">
            {calendarDays.map((day, idx) => {
              const dayPlans = getPlansForDate(day.date);
              const today = isToday(day.date);
              
              return (
                <div
                  key={idx}
                  className={`min-h-[60px] md:min-h-[120px] border-b border-r border-zinc-800/50 p-2 md:p-1 ${
                    !day.isCurrentMonth ? "hidden md:block bg-zinc-900/30" : ""
                  } ${today ? "bg-purple-500/5" : ""} ${
                    dayPlans.length === 0 ? "hidden md:block" : ""
                  }`}
                  onClick={() => openCreateDialog(day.date)}
                >
                  <div className={`text-sm font-medium mb-1 px-1 ${
                    !day.isCurrentMonth ? "text-zinc-600" :
                    today ? "text-purple-400" : "text-zinc-300"
                  }`}>
                    {/* Mobile : "Lun 14", desktop : "14" — le header md affiche déjà le jour */}
                    <span className="md:hidden">
                      {DAYS[(day.date.getDay() + 6) % 7]} {day.date.getDate()}
                    </span>
                    <span className="hidden md:inline">{day.date.getDate()}</span>
                  </div>
                  
                  <div className="space-y-1">
                    {dayPlans.slice(0, 3).map(plan => {
                      const Icon = TYPE_ICONS[plan.content_type] || FileText;
                      return (
                        <div
                          key={plan.id}
                          className={`text-xs px-1.5 py-1 rounded truncate cursor-pointer hover:opacity-80 ${TYPE_COLORS[plan.content_type]}`}
                          onClick={(e) => { e.stopPropagation(); openEditDialog(plan); }}
                          title={plan.title}
                        >
                          <Icon className="w-3 h-3 inline mr-1" />
                          {plan.title}
                        </div>
                      );
                    })}
                    {dayPlans.length > 3 && (
                      <div className="text-xs text-zinc-500 px-1">
                        +{dayPlans.length - 3} autres
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Vue Liste */
        <div className="space-y-2">
          {filteredPlans.length === 0 ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
              <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-white mb-1">Aucun contenu planifié</h3>
              <p className="text-zinc-400 mb-4">Commencez à planifier votre stratégie de contenu</p>
              <Button onClick={() => openCreateDialog()} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" />
                Créer un contenu
              </Button>
            </div>
          ) : (
            filteredPlans.map(plan => {
              const Icon = TYPE_ICONS[plan.content_type] || FileText;
              return (
                <div
                  key={plan.id}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 hover:border-zinc-700 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${TYPE_COLORS[plan.content_type]?.split(' ')[0]}`}>
                        <Icon className={`w-5 h-5 ${TYPE_COLORS[plan.content_type]?.split(' ')[1]}`} />
                      </div>
                      <div>
                        <h3 className="font-medium text-white">{plan.title}</h3>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge className={STATUS_COLORS[plan.status]}>
                            {STATUS_LABELS[plan.status]}
                          </Badge>
                          <span className="text-xs text-zinc-500">
                            {plan.target_date ? new Date(plan.target_date).toLocaleDateString('fr-FR') : 'Non planifié'}
                          </span>
                          {plan.activity_name && (
                            <span className="text-xs text-zinc-500">• {plan.activity_name}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {plan.status === "idea" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openGenerateDialog(plan)}
                          className="text-purple-400 border-purple-500/30"
                          data-testid="generate-content-btn"
                        >
                          <Sparkles className="w-4 h-4 mr-1" />
                          Générer
                        </Button>
                      )}
                      {["ready", "scheduled"].includes(plan.status) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePublish(plan.id)}
                          className="text-emerald-400 border-emerald-500/30"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Publier
                        </Button>
                      )}
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid="plan-actions-menu">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-zinc-800 border-zinc-700">
                          <DropdownMenuItem onClick={() => openEditDialog(plan)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-zinc-700" />
                          {/* Actions Content Factory ↔ Planning */}
                          {plan.generated_content || plan.content_id ? (
                            <DropdownMenuItem 
                              onClick={() => openContentDialog(plan)}
                              className="text-blue-400"
                              data-testid="view-content-menu-item"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Voir le contenu
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              onClick={() => openGenerateDialog(plan)}
                              className="text-purple-400"
                              data-testid="generate-content-menu-item"
                            >
                              <Wand2 className="w-4 h-4 mr-2" />
                              Générer le contenu
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator className="bg-zinc-700" />
                          <DropdownMenuItem 
                            onClick={() => handleDelete(plan.id)}
                            className="text-zinc-400 hover:text-zinc-200"
                          >
                            <Archive className="w-4 h-4 mr-2" />
                            Archiver
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Dialog Création */}
      <ContentPlanDialog
        open={createDialogOpen}
        onClose={() => { setCreateDialogOpen(false); setSelectedDate(null); }}
        activities={activities}
        connections={connections}
        defaultDate={selectedDate}
        onSuccess={loadData}
      />

      {/* Dialog Édition */}
      {selectedPlan && (
        <ContentPlanDialog
          open={editDialogOpen}
          onClose={() => { setEditDialogOpen(false); setSelectedPlan(null); }}
          plan={selectedPlan}
          activities={activities}
          connections={connections}
          onSuccess={loadData}
          onDelete={handlePurge}
          onGenerate={() => {
            setEditDialogOpen(false);
            openGenerateDialog(selectedPlan);
          }}
          onViewContent={() => {
            setEditDialogOpen(false);
            openContentDialog(selectedPlan);
          }}
        />
      )}

      {/* Dialog Génération de Contenu */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-400" />
              Générer le contenu
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {selectedPlan?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-400">Ton</Label>
              <Select value={generateTone} onValueChange={setGenerateTone}>
                <SelectTrigger className="bg-zinc-800/50 border-zinc-700 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="professional">Professionnel</SelectItem>
                  <SelectItem value="casual">Décontracté</SelectItem>
                  <SelectItem value="formal">Formel</SelectItem>
                  <SelectItem value="friendly">Amical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-zinc-400">Instructions supplémentaires (optionnel)</Label>
              <Textarea
                value={generateInstructions}
                onChange={(e) => setGenerateInstructions(e.target.value)}
                placeholder="Ex: Inclure des exemples concrets, mentionner les aides disponibles..."
                className="bg-zinc-800/50 border-zinc-700 mt-1 min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setGenerateDialogOpen(false)}
              className="border-zinc-700"
            >
              Annuler
            </Button>
            <Button
              onClick={handleGenerateContent}
              disabled={generating}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Génération...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Générer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Vue du Contenu */}
      <Dialog open={contentDialogOpen} onOpenChange={setContentDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-400" />
              {viewingContent?.title}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {TYPE_LABELS[viewingContent?.content_type] || viewingContent?.content_type}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {viewingContent?.generated_content ? (
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 max-h-[50vh] overflow-y-auto pr-6">
                <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-sans">
                  {viewingContent.generated_content}
                </pre>
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucun contenu généré</p>
                <Button
                  onClick={() => {
                    setContentDialogOpen(false);
                    openGenerateDialog(viewingContent);
                  }}
                  className="mt-4 bg-purple-600 hover:bg-purple-700"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  Générer maintenant
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setContentDialogOpen(false)}
              className="border-zinc-700"
            >
              Fermer
            </Button>
            {viewingContent?.generated_content && (
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(viewingContent.generated_content);
                  toast.success("Contenu copié!");
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Copier le contenu
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Dialog création/édition de contenu
function ContentPlanDialog({ open, onClose, plan, activities, connections, defaultDate, onSuccess, onGenerate, onViewContent, onDelete }) {
  const isEdit = !!plan;
  const hasContent = plan?.generated_content || plan?.content_id;
  const [deleting, setDeleting] = useState(false);
  
  const [title, setTitle] = useState("");
  const [contentType, setContentType] = useState("article");
  const [description, setDescription] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [targetChannel, setTargetChannel] = useState("");
  const [activityId, setActivityId] = useState("");
  const [priority, setPriority] = useState("medium");
  const [status, setStatus] = useState("idea");
  const [seoKeywords, setSeoKeywords] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (plan) {
        setTitle(plan.title || "");
        setContentType(plan.content_type || "article");
        setDescription(plan.description || "");
        setTargetDate(plan.target_date?.split('T')[0] || "");
        setTargetChannel(plan.target_channel || "");
        setActivityId(plan.activity_id || "");
        setPriority(plan.priority || "medium");
        setStatus(plan.status || "idea");
        setSeoKeywords(plan.seo_keywords?.join(", ") || "");
        setNotes(plan.notes || "");
      } else {
        setTitle("");
        setContentType("article");
        setDescription("");
        setTargetDate(defaultDate ? defaultDate.toISOString().split('T')[0] : "");
        setTargetChannel("");
        setActivityId("");
        setPriority("medium");
        setStatus("idea");
        setSeoKeywords("");
        setNotes("");
      }
    }
  }, [open, plan, defaultDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        title,
        content_type: contentType,
        description: description || null,
        target_date: targetDate ? `${targetDate}T09:00:00Z` : null,
        target_channel: targetChannel || null,
        activity_id: activityId || null,
        priority,
        seo_keywords: seoKeywords ? seoKeywords.split(",").map(k => k.trim()) : [],
        notes: notes || null,
      };

      if (isEdit) {
        data.status = status;
        await axios.patch(`${API}/api/marketing/editorial/${plan.id}`, data);
      } else {
        await axios.post(`${API}/api/marketing/editorial`, data);
      }

      onSuccess();
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Supprimer définitivement ce contenu du planning ?")) return;
    
    setDeleting(true);
    try {
      await onDelete(plan.id);
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto pr-6">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            {isEdit ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5 text-purple-400" />}
            {isEdit ? "Modifier le contenu" : "Nouveau contenu"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <Label className="text-zinc-400">Titre *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Article sur les aides à la rénovation"
              className="bg-zinc-800/50 border-zinc-700 mt-1"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400">Type de contenu</Label>
              <Select value={contentType} onValueChange={setContentType}>
                <SelectTrigger className="bg-zinc-800/50 border-zinc-700 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {Object.entries(TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-400">Priorité</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="bg-zinc-800/50 border-zinc-700 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="low">Basse</SelectItem>
                  <SelectItem value="medium">Moyenne</SelectItem>
                  <SelectItem value="high">Haute</SelectItem>
                  <SelectItem value="urgent">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-zinc-400">Description</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Décrivez le contenu à créer..."
              className="w-full bg-zinc-800/50 border border-zinc-700 rounded-md px-3 py-2 mt-1 text-white text-sm min-h-[80px]"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400">Date cible</Label>
              <Input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="bg-zinc-800/50 border-zinc-700 mt-1"
              />
            </div>

            {isEdit && (
              <div>
                <Label className="text-zinc-400">Statut</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="bg-zinc-800/50 border-zinc-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {Object.entries(STATUS_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div>
            <Label className="text-zinc-400">Activité</Label>
            <Select value={activityId || "none"} onValueChange={(v) => setActivityId(v === "none" ? "" : v)}>
              <SelectTrigger className="bg-zinc-800/50 border-zinc-700 mt-1">
                <SelectValue placeholder="Toutes activités" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="none">Toutes activités</SelectItem>
                {activities.map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-zinc-400">Canal de publication</Label>
            <Select value={targetChannel || "none"} onValueChange={(v) => setTargetChannel(v === "none" ? "" : v)}>
              <SelectTrigger className="bg-zinc-800/50 border-zinc-700 mt-1">
                <SelectValue placeholder="Sélectionner un canal" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="none">Non défini</SelectItem>
                {connections.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-zinc-400">Mots-clés SEO (séparés par virgule)</Label>
            <Input
              value={seoKeywords}
              onChange={(e) => setSeoKeywords(e.target.value)}
              placeholder="Ex: rénovation, aides, MaPrimeRénov"
              className="bg-zinc-800/50 border-zinc-700 mt-1"
            />
          </div>

          <div>
            <Label className="text-zinc-400">Notes</Label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes internes..."
              className="w-full bg-zinc-800/50 border border-zinc-700 rounded-md px-3 py-2 mt-1 text-white text-sm min-h-[60px]"
            />
          </div>
        </form>

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            {/* Boutons d'action Content Factory ↔ Planning */}
            {isEdit && (
              hasContent ? (
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={onViewContent}
                  className="border-blue-500/30 text-blue-400 hover:bg-blue-500/20"
                  data-testid="dialog-view-content-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Voir le contenu
                </Button>
              ) : (
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={onGenerate}
                  className="border-purple-500/30 text-purple-400 hover:bg-purple-500/20"
                  data-testid="dialog-generate-content-btn"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  Générer
                </Button>
              )
            )}
          </div>
          <div className="flex gap-2">
            {isEdit && onDelete && (
              <Button 
                type="button"
                variant="outline" 
                onClick={handleDelete}
                disabled={deleting}
                className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                data-testid="dialog-delete-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Supprimer
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button 
              onClick={handleSubmit}
              disabled={saving || !title}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isEdit ? "Enregistrer" : "Créer"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
